import { withErrorHandling } from "@entrilia/shared/api/axiosHelper.ts";
import type { ApiResponse } from "@entrilia/shared/api/types.ts";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext.ts";
import { logError } from "@entrilia/shared/logging.ts";
import type { AnyObject } from "@entrilia/shared/types.ts";
import { Box, Stack } from "@mui/material";
import { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import type { ConditionConfiguration } from "../../../../../../../api/types/fieldConfigurationTypes.ts";
import type { RunListReportRequest } from "../../../../../../../api/types/svTypes.ts";
import type { BiApiClient } from "../../../contexts/ApiClientProviderContext.ts";
import { useEnhancedBiApiClientProvider } from "../../../contexts/ApiClientProviderContext.ts";
import { useBiApiClientProvider } from "../../../contexts/BiApiClientProviderContext.ts";
import { useBiCommonDataContext } from "../../../contexts/BiCommonDataContext.ts";
import { type FetchRowsPayload } from "@entrilia/shared/components/reporting/text-filter-grid/useGridDataSourceWithRangePaging.ts";
import { useAppDispatch } from "../../../store/hooks/useAppDispatch.ts";
import { selectSessionId } from "../../../store/selectors/currentReportSelectors.ts";
import { selectLogQueries } from "../../../store/selectors/devToolsSelectors.ts";
import { createDevToolsAction } from "../../../store/utils/storeUtils.ts";
import ExcelReportExport from "../../common/export/ExcelReportExport.tsx";
import { getListExportWarning } from "../../common/export/exportWarnings.ts";
import PreviewActions from "../../viewer/common/PreviewActions.tsx";
import PreviewComponentAlert from "../../viewer/common/PreviewComponentAlert.tsx";
import ViewContainer from "../../viewer/common/ViewContainer.tsx";
import ViewerParameters from "../../viewer/common/ViewerParameters.tsx";
import type { ListRunState } from "../../viewer/storage/runState.ts";
import RecordsCounter from "../common/drillDown/RecordsCounter.tsx";
import { rebuildSortingFields, type SortingFieldModel } from "../tabular/preview/rebuildSortingFields.ts";
import useListReportDataSource from "./hooks/useListReportDataSource.ts";
import ListGrid from "./preview/ListGrid.tsx";
import { getListAggregationModel, getListColumns, toListRow } from "./utils/listGridHelper.ts";
import { getSortModel, toSortConfigurations } from "./utils/listSortHelper.ts";
import type { ListPageResponse } from "./utils/prepareListReport.ts";

interface Props {
  runState: ListRunState;
}

interface ListRunPayload extends AnyObject {
  reportId: string | undefined;
  sessionId: string;
  conditions: ConditionConfiguration[];
  useQueryLogging: boolean;
}

const ListViewerContainer = ({ runState }: Props) => {
  const { runListReport } = useEnhancedBiApiClientProvider();
  const { conditions, dimensions, reportId, dataSetId, onSortingUpdate } = useBiCommonDataContext();
  const dispatch = useAppDispatch();
  const { sendNotificationError } = useNotificationContext();
  const sessionId = useSelector(selectSessionId);
  const logQueries = useSelector(selectLogQueries);
  const runListReportSafe = useMemo(
    () => (runListReport ? withErrorHandling(runListReport) : undefined),
    [runListReport]
  );

  const parameterConditions = useMemo(
    () => conditions.filter((c) => c.config.parameter === true).map((c) => c.config),
    [conditions]
  );

  const payload = useMemo(
    (): ListRunPayload => ({
      reportId,
      sessionId,
      conditions: parameterConditions,
      useQueryLogging: logQueries === true,
    }),
    [reportId, sessionId, parameterConditions, logQueries]
  );
  const exportRequestConfig = useMemo(
    (): RunListReportRequest => ({
      reportId: reportId ?? "",
      sessionId,
      conditions: parameterConditions,
      sort: runState.sort.map((field) => field.config),
      range: {},
      useQueryLogging: logQueries === true,
    }),
    [parameterConditions, logQueries, reportId, runState.sort, sessionId]
  );

  const fetchRows = useCallback(
    async (params: FetchRowsPayload<ListRunPayload>): Promise<ApiResponse<ListPageResponse>> => {
      if (!runListReportSafe) {
        return { success: false };
      }
      const body: RunListReportRequest = {
        reportId: params.reportId ?? "",
        sessionId: params.sessionId,
        conditions: params.conditions,
        sort: toSortConfigurations(params.sortModel, runState.fields),
        range: params.range,
        useQueryLogging: params.useQueryLogging,
      };

      const [response, error] = await runListReportSafe(dataSetId, body, params.signal);
      if (error) {
        if (error.isCanceledRequest) {
          return { success: false };
        }
        logError(error, "[ListViewerContainer] fetchRows");
        sendNotificationError(error.message);
        return { success: false, error };
      }

      if (params.useQueryLogging && response.loggingItems !== undefined) {
        dispatch(createDevToolsAction("update", { loggingItems: response.loggingItems }));
      }

      return {
        success: true,
        data: {
          items: response.rows.map(toListRow),
          total: response.total,
          failedMeasures: response.failedMeasures,
          grandTotalItem: toListRow(response.grandTotalItem ?? []),
        },
      };
    },
    [runListReportSafe, dataSetId, runState.fields, dispatch, sendNotificationError]
  );

  const requestedSortModel = useMemo(() => getSortModel(runState.sort), [runState.sort]);
  const configuration = useMemo(
    () => ({ payload, fields: runState.fields, settings: runState.settings }),
    [payload, runState.fields, runState.settings]
  );
  const result = useListReportDataSource(configuration, requestedSortModel, fetchRows);
  const { resultId, error, dataSource, totalRows, isLoading, sortModel, onRefresh } = result;
  const { requestRunListExport } = useBiApiClientProvider<BiApiClient>();

  const displayedFields = result.configuration.fields;
  const showGrandTotal = result.configuration.settings.showGrandTotal === true;
  const columns = useMemo(
    () => getListColumns(displayedFields, showGrandTotal, result.failedMeasures),
    [displayedFields, showGrandTotal, result.failedMeasures]
  );
  const aggregationModel = useMemo(
    () => getListAggregationModel(displayedFields, showGrandTotal),
    [displayedFields, showGrandTotal]
  );
  const pinnedColumns = useMemo(() => ({ left: runState.settings.freezedColumns }), [runState.settings.freezedColumns]);

  const handleSortingChanged = useCallback(
    (model: SortingFieldModel) => {
      const sorts = rebuildSortingFields(model, runState.fields, runState.sort);
      onSortingUpdate?.(sorts);
    },
    [runState.fields, runState.sort, onSortingUpdate]
  );

  return (
    <>
      <ExcelReportExport
        mode="run"
        requestConfig={exportRequestConfig}
        dimensions={dimensions}
        requestExport={requestRunListExport}
        warning={getListExportWarning(isLoading, totalRows)}
      />
      <ViewContainer
        Parameters={
          runState.showParameters ? (
            <ViewerParameters
              fieldsWithConditions={runState.fieldsWithCustomConditionParameters}
              initialDetailedView={runState.settings.detailedView}
            />
          ) : (
            <></>
          )
        }
        Preview={
          <Stack sx={{ flex: 1, minHeight: 0, gap: 1 }}>
            <PreviewComponentAlert error={undefined} failedMeasures={result.failedMeasures} />
            <ListGrid
              loading={isLoading}
              columns={columns}
              resultId={resultId}
              error={error}
              dataSource={dataSource}
              sortModel={sortModel}
              aggregationModel={aggregationModel}
              pinnedColumns={pinnedColumns}
              onSortingChanged={handleSortingChanged}
            />
            <Box>
              <RecordsCounter totalCount={totalRows ?? 0} align="left" />
            </Box>
          </Stack>
        }
        ActionPanel={<PreviewActions onRefresh={onRefresh} />}
      />
    </>
  );
};

export default ListViewerContainer;
